import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO, { createBreadcrumbSchema, createFAQSchema, softwareApplicationSchema } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, Zap, Image, Package, Camera, Layers, Clock, Star, Award } from 'lucide-react';

const AIImageGeneratorEbayUK = () => {
  const faqs = [
    {
      question: "What image sizes does eBay UK require for listings?",
      answer: "eBay.co.uk recommends images at least 500x500 pixels, with 1600x1600 being ideal for the zoom functionality. Our AI generator creates high-resolution images that exceed these requirements for professional-looking UK listings."
    },
    {
      question: "Can I create product photos for vintage and antique items?",
      answer: "Absolutely! Our AI can enhance and style photos of vintage, antique, and pre-owned items popular on eBay UK. Create professional lifestyle shots and clean backgrounds for collectibles and unique finds."
    },
    {
      question: "Are AI-generated images allowed on eBay UK?",
      answer: "Yes, AI-generated images are permitted for product photography on eBay.co.uk. Our platform provides full commercial rights, making images completely safe for your UK eBay listings."
    },
    {
      question: "How can AI help with bulk eBay UK listings?",
      answer: "UK power sellers can generate hundreds of consistent product images in minutes. Create uniform backgrounds, consistent styling, and professional quality across your entire inventory without expensive photography."
    }
  ];

  const features = [
    {
      icon: <Image className="w-6 h-6" />,
      title: "eBay UK Optimised",
      description: "Generate 1600x1600px images perfect for eBay.co.uk's zoom feature and gallery display"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "White Background Photos",
      description: "Create clean, professional white background product shots that meet eBay UK standards"
    },
    {
      icon: <Package className="w-6 h-6" />,
      title: "Multi-Angle Views",
      description: "Generate multiple angles and perspectives of products for comprehensive UK listings"
    },
    {
      icon: <Layers className="w-6 h-6" />,
      title: "Lifestyle Imagery",
      description: "Create in-use and lifestyle images that help UK buyers envision owning your products"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Bulk Generation",
      description: "UK power sellers can create 100+ professional images in minutes, not hours"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "UK Market Styled",
      description: "Images styled for UK consumer preferences and eBay.co.uk marketplace trends"
    }
  ];

  const useCases = [
    "eBay UK power sellers managing large inventories",
    "Vintage and antique dealers across the UK",
    "Electronics resellers creating clean product shots",
    "UK clothing and fashion eBay stores",
    "Automotive parts and accessories sellers",
    "Home goods and furniture resellers in the UK",
    "Sports memorabilia and trading card sellers",
    "Car boot sale and charity shop resellers"
  ];

  return (
    <>
      <SEO
        title="AI Image Generator for eBay UK | Professional eBay.co.uk Photos"
        description="Create professional eBay UK listing photos with AI. White backgrounds, lifestyle shots, multi-angle views for eBay.co.uk sellers. Used by 6,000+ UK eBay power sellers."
        keywords="ai image generator ebay uk, ebay.co.uk product photos, ebay uk listing images, ebay uk seller photography, ebay uk photo generator, ebay uk power seller tools, best images for ebay uk"
        url="/ai-image-generator-ebay-uk"
        jsonLd={[
          softwareApplicationSchema,
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'AI Image Generator for eBay UK', url: '/ai-image-generator-ebay-uk' }
          ]),
          createFAQSchema(faqs)
        ]}
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-indigo-500/5 to-background" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 text-foreground border-0">
                Trusted by 6,000+ UK eBay Sellers
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                AI Image Generator for eBay UK
                <span className="block bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent drop-shadow-sm">
                  Professional eBay.co.uk Photos
                </span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Create stunning eBay UK listing images with AI. Professional white backgrounds, lifestyle shots, 
                and multi-angle views in seconds. Perfect for UK power sellers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:opacity-90 text-white">
                  <Link to="/try-generator">
                    Create eBay UK Images Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/gallery">View Product Examples</Link>
                </Button>
              </div>
              
              {/* Trust Badges */}
              <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span>4.9/5 from UK sellers</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Full Commercial Rights</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-indigo-500" />
                  <span>Images in Seconds</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Why UK eBay Sellers Choose Our AI Generator
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="border border-transparent bg-gradient-to-br from-blue-500/5 to-indigo-500/5 hover:from-blue-500/10 hover:to-indigo-500/10 hover:border-blue-500/20 transition-all shadow-sm hover:shadow-md">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/20 to-indigo-500/20 flex items-center justify-center text-foreground mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                Perfect For Every UK eBay Seller
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {useCases.map((useCase, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-gradient-to-r from-blue-500/5 to-indigo-500/5">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span>{useCase}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Cross-links */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-8">
              Selling on Other UK Platforms?
            </h2>
            <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
              <Button asChild variant="outline">
                <Link to="/ai-image-generator-amazon-uk">AI for Amazon UK</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/ai-image-generator-depop">AI for Depop</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/ai-image-generator-for-etsy">AI for Etsy UK</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-0 bg-gradient-to-br from-blue-500/5 to-indigo-500/5">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Start Creating Professional eBay UK Listings Today
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join 6,000+ UK eBay sellers using AI to create professional product photos. 
                No photography skills or expensive equipment needed.
              </p>
              <Button asChild size="lg" className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:opacity-90 text-white">
                <Link to="/try-generator">
                  Generate eBay UK Images Free <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default AIImageGeneratorEbayUK;
